import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ChevronLeft, ChevronRight } from "lucide-react";

import Reveal from "@/components/public/Reveal";
import { publicApi } from "@/lib/api";
import { FALLBACK_TEAM, getTeamMedia } from "@/lib/company";

export default function AboutPage() {
  const [page, setPage] = useState(null);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    publicApi.track({ event_type: "page_view", page: "/about" }).catch(() => {});
    publicApi.getPage("about").then(setPage).catch(() => {});
    publicApi.getTeam().then(setTeam).catch(() => {});
  }, []);

  useEffect(() => {
    if (!window.location.hash) return;

    const targetId = decodeURIComponent(window.location.hash.replace("#", ""));
    const timer = window.setTimeout(() => {
      const node = document.getElementById(targetId);
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 180);

    return () => window.clearTimeout(timer);
  }, [team.length]);

  const hero = page?.content?.hero || {
    headline: "The meaning of Minga.",
    subheadline: "Collective work between Seattle and Quito.",
    background:
      "/assets/minga/projects/waters-residence/06-social-area.jpg",
  };

  const meaning = page?.content?.meaning;
  const story = page?.content?.story;
  const teamIntro = page?.content?.team_gallery_intro;
  const displayedTeam = team.length ? team : FALLBACK_TEAM;

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[80svh] min-h-[520px] w-full overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${hero.background})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        <div className="absolute inset-0 bg-gradient-to-b from-ink-black/70 via-ink-black/40 to-ink-black" />

        <div className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <p className="eyebrow">{hero.eyebrow || "[ About Us ]"}</p>

          <h1 className="hero-headline mt-6 max-w-4xl">
            {hero.headline}
          </h1>

          <p className="body-text mt-6 max-w-2xl text-brand-white/85 md:text-lg">
            {hero.subheadline}
          </p>
        </div>
      </section>

      {/* Meaning */}
      {meaning && (
        <section className="section-pad max-w-[1440px] mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <Reveal className="lg:col-span-5">
              <p className="eyebrow">[ Meaning ]</p>

              <h2 className="section-headline mt-4">
                {meaning.headline}
              </h2>
            </Reveal>

            <Reveal className="lg:col-span-7" delay={80}>
              <p className="body-text text-lg">
                {meaning.paragraph}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Story */}
      {story && (
        <section className="section-pad bg-ink-charcoal/70 border-y border-white/6">
          <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 lg:gap-16 items-center">
            <Reveal className="md:col-span-6 relative aspect-[4/5] overflow-hidden bg-ink-graphite">
              <img
                src={story.image}
                alt="From Quito to Seattle"
                loading="lazy"
                className="h-full w-full object-cover"
              />

              <div className="absolute top-5 left-5 eyebrow bg-ink-black/60 px-3 py-1.5">
                Quito → Seattle
              </div>
            </Reveal>

            <Reveal className="md:col-span-6" delay={80}>
              <p className="eyebrow">[ Studio Story ]</p>

              <h2 className="section-headline mt-4">
                {story.headline}
              </h2>

              <p className="body-text mt-6">
                {story.paragraph}
              </p>

              <div className="mt-8 flex items-center gap-3 text-sm text-brand-gray">
                <MapPin className="h-4 w-4 text-brand-yellow" />
                Seattle, WA — Quito, Ecuador
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Team Gallery */}
      <section
        id="team-gallery"
        className="section-pad max-w-[1440px] mx-auto px-5 md:px-10 scroll-mt-28"
      >
        <Reveal>
          <p className="eyebrow">{teamIntro?.eyebrow || "[ Team Gallery ]"}</p>

          <h2 className="section-headline mt-4">
            {teamIntro?.headline || "Portraits of the people behind Minga."}
          </h2>
        </Reveal>

        <div className="mt-14 space-y-16 md:space-y-20">
          {displayedTeam.map((member, index) => (
            <TeamGalleryMember
              key={member.id || member.name}
              member={member}
              index={index}
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad bg-brand-yellow text-ink-black">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="eyebrow justify-center !text-ink-black/70">[ Let’s Talk ]</p>

            <h2 className="section-headline mt-4 text-ink-black">
              Ready to start a conversation?
            </h2>

            <div className="mt-8 inline-flex">
              <Link to="/contact" className="btn-ink">
                Contact Us <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function TeamGalleryMember({ member, index }) {
  const media = getTeamMedia(member);

  // Normalize gallery to color-only image entries.
  // Supports legacy {bw,color} pairs and simple string/url entries.
  const normalizedGallery = (media.gallery?.length
    ? media.gallery
    : [{ color: media.hoverImage || member.hover_image || media.portrait || member.portrait }]
  )
    .map((item) => {
      if (!item) return null;
      if (typeof item === "string") return { image: item };
      const image =
        item.color || item.image || item.url || item.hoverImage || item.hover_image;
      const caption = item.caption || null;
      return image ? { image, caption } : null;
    })
    .filter(Boolean);

  const [activeIndex, setActiveIndex] = useState(0);

  const bio = member.full_bio || member.short_bio;
  const currentPortrait = normalizedGallery[activeIndex];

  const goToPrevious = () => {
    if (!normalizedGallery.length) return;

    setActiveIndex((current) =>
      current === 0 ? normalizedGallery.length - 1 : current - 1
    );
  };

  const goToNext = () => {
    if (!normalizedGallery.length) return;

    setActiveIndex((current) =>
      current === normalizedGallery.length - 1 ? 0 : current + 1
    );
  };

  if (!normalizedGallery.length) return null;

  return (
    <Reveal
      delay={index * 90}
      className="relative overflow-hidden border border-white/10 bg-ink-charcoal/65 surface"
    >
      <article className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[430px_minmax(0,1fr)]">
          {/* Bio panel */}
          <aside className="relative z-10 flex flex-col justify-between border-b border-white/10 bg-ink-black/70 p-7 sm:p-9 lg:min-h-[680px] lg:border-b-0 lg:border-r lg:border-white/10 xl:p-11">
            <div>
              <p className="mb-5 text-xs font-bold uppercase tracking-[0.28em] text-brand-yellow">
                {member.role}
              </p>

              <h3 className="max-w-[360px] text-5xl font-bold leading-[0.95] tracking-[-0.06em] text-brand-white sm:text-6xl lg:text-7xl">
                {member.name}
              </h3>

              {member.credentials ? (
                <p className="mt-6 text-xs font-bold uppercase tracking-[0.22em] text-brand-yellow">
                  {member.credentials}
                </p>
              ) : null}

              <p className="mt-7 max-w-[360px] text-base font-medium leading-[1.75] text-brand-white/90 sm:text-lg lg:text-[18px]">
                {bio}
              </p>
            </div>

            <div className="mt-10 max-w-[300px] border-t border-brand-yellow pt-5">
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-brand-white/75">
                {normalizedGallery.length}{" "}
                {normalizedGallery.length === 1 ? "portrait" : "portraits"} · curated selection
              </p>
            </div>
          </aside>

          {/* Portrait viewer */}
          <div className="relative flex min-h-[560px] items-center justify-center overflow-hidden px-5 py-10 sm:px-10 lg:min-h-[680px] lg:px-14 xl:px-20">
            {normalizedGallery.length > 1 ? (
              <button
                type="button"
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-ink-black/85 text-brand-white shadow-2xl backdrop-blur transition-all duration-300 hover:border-brand-yellow hover:text-brand-yellow hover:scale-105 sm:left-8"
                aria-label={`Previous ${member.name} portrait`}
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
            ) : null}

            <div className="relative w-full max-w-[560px]">
              <PortraitCard
                key={`${member.id || member.name}-${activeIndex}`}
                item={currentPortrait}
                member={member}
                index={activeIndex}
              />

              <div className="mt-6 flex items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  {normalizedGallery.map((_, dotIndex) => (
                    <button
                      key={`${member.id || member.name}-dot-${dotIndex}`}
                      type="button"
                      onClick={() => setActiveIndex(dotIndex)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        activeIndex === dotIndex
                          ? "w-8 bg-brand-yellow"
                          : "w-2 bg-white/25 hover:bg-white/50"
                      }`}
                      aria-label={`Go to portrait ${dotIndex + 1}`}
                    />
                  ))}
                </div>

                <p className="shrink-0 text-xs font-bold uppercase tracking-[0.24em] text-brand-white/65">
                  {String(activeIndex + 1).padStart(2, "0")} /{" "}
                  {String(normalizedGallery.length).padStart(2, "0")}
                </p>
              </div>
            </div>

            {normalizedGallery.length > 1 ? (
              <button
                type="button"
                onClick={goToNext}
                className="absolute right-4 top-1/2 z-30 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-ink-black/85 text-brand-white shadow-2xl backdrop-blur transition-all duration-300 hover:border-brand-yellow hover:text-brand-yellow hover:scale-105 sm:right-8"
                aria-label={`Next ${member.name} portrait`}
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            ) : null}
          </div>
        </div>
      </article>
    </Reveal>
  );
}

function PortraitCard({ item, member, index }) {
  const image = item.image;
  if (!image) return null;

  return (
    <div className="team-gallery-tile relative aspect-[3/4] w-full overflow-hidden border border-white/10 bg-ink-graphite shadow-2xl surface-hover">
      <img
        src={image}
        alt={`${member.name} portrait ${index + 1}`}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[850ms] hover:scale-[1.03]"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-ink-black/70 via-transparent to-transparent opacity-90" />

      <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-3">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-white/85">
          {String(index + 1).padStart(2, "0")}
        </span>

        <span className="h-8 w-8 border-t-2 border-r-2 border-brand-yellow opacity-80" />
      </div>
    </div>
  );
}